'use client'

import { useQueries } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { avgPurchasePriceUsd } from '@/features/dashboard/lib/balance-entry-price'
import {
  getBalanceTickerData,
  isUsdQuotedTicker,
  mergeSameCurrencyBalances,
  resolveBalancePriceAndEquity,
} from '@/features/dashboard/lib/balance-pricing'
import {
  BalanceTickerData,
  balanceTickerKey,
  createBalanceTickerBatcher,
} from '@/features/dashboard/lib/balance-ticker-batcher'
import type { BalanceRow } from '@/features/dashboard/lib/balance-to-position-row'
import { buildSpotPairMap } from '@/features/dashboard/lib/spot-pair-map'
import { toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { safeParseFloat } from '@/lib/utils'
import { accountExchangeKeys, fetchAccountExchanges } from '@/service/account-exchanges.hook'
import { instrumentListQueryOptions } from '@/service/instrument.hook'
import { useMultiTickerStream } from '@/service/ws/use-ticker-stream'
import { allBalancesAtom } from '@/state/account'
import type { PortfolioData } from '@/types/generated/common/account'
import {
  Exchange,
  Instrument,
  MarketDataType,
  TickerList,
  TradeVenue,
} from '@/types/generated/common/types'

/**
 * `accountIds` handling has three explicit branches, not one `||`
 * expression — `!balance.accountId || accountIds.includes(...)` alone would
 * let untagged rows through even when `accountIds` is an explicitly-empty
 * array (zero selected/active accounts), which would silently violate a
 * "zero accounts -> no data" requirement (e.g. Account Cards' "All Accounts"
 * mode with zero ACTIVE accounts must show nothing, not leak untagged rows).
 */
function filterByAccountIds(
  balances: PortfolioData[],
  accountIds?: string[] | null
): PortfolioData[] {
  if (accountIds == null) return balances
  if (accountIds.length === 0) return []
  const set = new Set(accountIds)
  return balances.filter((b) => !b.accountId || set.has(b.accountId))
}

/**
 * Aggregates spot balances across every linked account/venue — mirrors how
 * derivative positions already aggregate cross-account via `allPositionsAtom`
 * (see position-table.tsx). Each distinct trade venue among the held balances
 * gets its own primary-exchange + instrument-list lookup, so a spot pair (e.g.
 * BTC -> BTC_USDC) resolves against that balance's own venue instead of a
 * single card's venue — two accounts on different venues holding the same
 * currency can resolve to different tradeable pairs.
 *
 * @param accountIds Scope to these accounts only. `undefined`/`null` (the
 * default) means every account — unchanged from this hook's original,
 * no-arg-only behavior. An explicitly empty array means zero accounts (see
 * `filterByAccountIds`), not "every account."
 */
export function useBalanceRows(accountIds?: string[] | null): BalanceRow[] {
  const rawBalances = useAtomValue(allBalancesAtom)

  // Stable content-based key so a new-but-equal `accountIds` array reference
  // from the caller doesn't thrash the memoized filter and everything
  // downstream of it (venue/instrument `useQueries`, ticker subscriptions).
  const accountIdsKey = accountIds == null ? null : [...accountIds].sort().join(',')
  const allBalances = useMemo(
    () => filterByAccountIds(rawBalances, accountIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- accountIdsKey is the stable proxy for accountIds' content
    [rawBalances, accountIdsKey]
  )

  const tradeVenues = useMemo(
    () =>
      [...new Set(allBalances.map((b) => b.tradeVenue))].filter(
        (tv): tv is TradeVenue => tv !== undefined && tv !== TradeVenue.TRADE_VENUE_UNSPECIFIED
      ),
    [allBalances]
  )

  // `combine` must be referentially stable: an inline one re-runs on every
  // render and returns a fresh Map, which invalidates every memo below it and
  // so every row identity on every render of the caller.
  const combineExchanges = useCallback(
    (results: { data?: Exchange[] }[]) => {
      const map = new Map<TradeVenue, Exchange>()
      tradeVenues.forEach((tradeVenue, i) => {
        const exchanges = results[i]?.data
        map.set(
          tradeVenue,
          exchanges && exchanges.length > 0
            ? exchanges[0]
            : toExchangeEnum(tradeVenueToExchangeKey(tradeVenue))
        )
      })
      return map
    },
    [tradeVenues]
  )
  const primaryExchangeByVenue = useQueries({
    queries: tradeVenues.map((tradeVenue) => ({
      queryKey: accountExchangeKeys.byVenue(tradeVenue),
      queryFn: () => fetchAccountExchanges(tradeVenue),
      staleTime: 60 * 60 * 1000,
    })),
    combine: combineExchanges,
  })

  const exchangesToFetch = useMemo(
    () => [...new Set(primaryExchangeByVenue.values())],
    [primaryExchangeByVenue]
  )

  const combineInstruments = useCallback(
    (results: { data?: { instruments?: Instrument[] } }[]) => {
      const map = new Map<Exchange, Instrument[]>()
      exchangesToFetch.forEach((exchange, i) => {
        map.set(exchange, results[i]?.data?.instruments ?? [])
      })
      return map
    },
    [exchangesToFetch]
  )
  const instrumentsByExchange = useQueries({
    queries: exchangesToFetch.map((exchange) => ({
      ...instrumentListQueryOptions(exchange),
      refetchInterval: 60 * 60 * 1000,
    })),
    combine: combineInstruments,
  })

  const spotPairMapByVenue = useMemo(() => {
    const map = new Map<TradeVenue, Map<string, string>>()
    tradeVenues.forEach((tradeVenue) => {
      const exchange = primaryExchangeByVenue.get(tradeVenue)
      const instruments = exchange !== undefined ? (instrumentsByExchange.get(exchange) ?? []) : []
      map.set(tradeVenue, buildSpotPairMap(instruments))
    })
    return map
  }, [tradeVenues, primaryExchangeByVenue, instrumentsByExchange])

  // Ticker cache keyed by `${exchange}:${symbol}` — NOT bare currency. Two
  // accounts on different venues can both hold e.g. "BTC"; a currency-only key
  // would let whichever venue's ticker message arrives last silently overwrite
  // the price applied to both accounts' rows. Keying by the same
  // exchange+symbol pair the subscription itself uses means every row's own
  // resolved (exchange, symbol) maps to exactly one cache entry, with no
  // cross-venue collision possible.
  const [tickerDataMap, setTickerDataMap] = useState<Map<string, BalanceTickerData>>(new Map())

  // Coalesced write path (see balance-ticker-batcher.ts): the WS handler pushes
  // every message here; state — and so every row identity downstream — changes
  // at most once per flush window.
  const batcherRef = useRef<ReturnType<typeof createBalanceTickerBatcher> | null>(null)
  if (!batcherRef.current) {
    batcherRef.current = createBalanceTickerBatcher({
      onFlush: (pending) => {
        setTickerDataMap((prev) => {
          let next: Map<string, BalanceTickerData> | null = null
          for (const [key, data] of pending) {
            const current = prev.get(key)
            if (current?.coinPrice === data.coinPrice && current.todayPnl === data.todayPnl)
              continue
            next ??= new Map(prev)
            next.set(key, data)
          }
          return next ?? prev
        })
      },
    })
  }
  useEffect(() => {
    const batcher = batcherRef.current
    return () => batcher?.destroy()
  }, [])

  const mergedBalances = useMemo(() => mergeSameCurrencyBalances(allBalances), [allBalances])

  // Venue/symbol resolution + price/equity are computed exactly ONCE per
  // balance here (O(n)) and reused for sorting, ticker-payload dedup, and the
  // final row build below — previously each of those three steps recomputed
  // them independently (the sort comparator alone re-derived them on every
  // O(n log n) comparison), which was pure redundant work for identical
  // results every time.
  const resolvedEntries = useMemo(
    () =>
      mergedBalances.map((bal) => {
        const exchange = primaryExchangeByVenue.get(bal.tradeVenue) ?? Exchange.EXCHANGE_UNSPECIFIED
        const resolvedSymbol =
          spotPairMapByVenue.get(bal.tradeVenue)?.get(bal.currency.toUpperCase()) ?? null
        const tickerKey = balanceTickerKey(exchange, resolvedSymbol ?? bal.currency)
        const ticker = getBalanceTickerData(tickerDataMap, tickerKey, bal.currency)
        const amount = safeParseFloat(bal.equity)
        const { coinPrice, equityValue } = resolveBalancePriceAndEquity(
          amount,
          bal.totalEquityUsd,
          ticker?.coinPrice,
          resolvedSymbol !== null,
          isUsdQuotedTicker(bal.currency, resolvedSymbol)
        )
        return {
          bal,
          exchange,
          resolvedSymbol,
          tickerKey,
          amount,
          coinPrice,
          equityValue,
          avgPriceUsd: avgPurchasePriceUsd(bal),
          todayPnl: ticker?.todayPnl ?? 0,
        }
      }),
    [mergedBalances, primaryExchangeByVenue, spotPairMapByVenue, tickerDataMap]
  )

  const sortedEntries = useMemo(
    () => [...resolvedEntries].sort((a, b) => b.equityValue - a.equityValue),
    [resolvedEntries]
  )

  // Ticker subscription payloads, deduped by (exchange, symbol) — multiple accounts
  // holding the same currency on the same venue would otherwise redundantly
  // resubscribe the identical channel. No separate currency fan-out map is needed:
  // tickerDataMap is keyed by this same (exchange, symbol) pair, so every row looks
  // its own price up directly.
  const tickerPayloads = useMemo(() => {
    const seen = new Set<string>()
    const payloads: { exchange: Exchange; symbol: string; dataType: MarketDataType }[] = []
    resolvedEntries.forEach((entry) => {
      if (entry.exchange === Exchange.EXCHANGE_UNSPECIFIED) return
      if (seen.has(entry.tickerKey)) return
      seen.add(entry.tickerKey)
      payloads.push({
        exchange: entry.exchange,
        symbol: entry.resolvedSymbol ?? entry.bal.currency,
        dataType: MarketDataType.MARKET_DATA_TYPE_TICKER,
      })
    })
    return payloads
  }, [resolvedEntries])

  useMultiTickerStream({
    connected: tickerPayloads.length > 0,
    payloads: tickerPayloads,
    onMessage: (message: TickerList) => {
      if (message.tickers) batcherRef.current?.push(message.tickers)
    },
  })

  return useMemo<BalanceRow[]>(
    () =>
      sortedEntries.map((entry) => ({
        currency: entry.bal.currency,
        accountId: entry.bal.accountId ?? '',
        amount: entry.amount,
        coinPrice: entry.coinPrice,
        equityValue: entry.equityValue,
        avgPriceUsd: entry.avgPriceUsd,
        todayPnl: entry.todayPnl,
        resolvedSymbol: entry.resolvedSymbol,
        tradeVenue: entry.bal.tradeVenue,
        exchange: entry.exchange,
      })),
    [sortedEntries]
  )
}
